import { AddIcon, ArrowForwardIcon } from '@chakra-ui/icons'
import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { colors } from '@hb/shared'
import React from 'react'
import { SolidActionButton } from '../Buttons'
import { StandaloneInput } from '../forms'
import { AddPaymentProps, MovePaymentProps, useAddPayment, useMovePayment } from './hooks'
const MovePayment = ({
  invoice,
  assessmentId,
  paymentId,
  initialFromId,
  initialToId,
  onClose,
}: MovePaymentProps) => {
  const {
    moveToId,
    moveFromId,
    setMoveToId,
    setMoveFromId,
    toInvoiceField,
    fromInvoiceField,
    hasMultipleUnsent,
    moveLoading,
    canMove,
    onMove,
  } = useMovePayment({
    invoice,
    assessmentId,
    paymentId,
    initialFromId,
    initialToId,
    onClose,
  })

  return (
    <Flex flexFlow="column" w="100%">
      <Flex p={2} borderBottom="1px solid #e0e0e0" w="100%" justify="space-between">
        <Text lineHeight={1} fontWeight={500} fontSize="md">
          Move Payment
        </Text>
      </Flex>
      <VStack bg="gray.50" px={3} pb={2} position="relative" align="start" spacing={2}>
        {!hasMultipleUnsent ? (
          <Box pt={1}>
            <Text fontStyle="italic" opacity={0.7} fontSize="md">
              No other unsent invoices
            </Text>
          </Box>
        ) : (
          <Flex pt={1} w="100%" flexFlow="column">
            <Text fontSize="md">From Invoice</Text>
            <StandaloneInput
              field={fromInvoiceField}
              theme="detailed"
              value={moveFromId}
              onChange={setMoveFromId}
            />
            <Text fontSize="md">To Invoice</Text>
            <StandaloneInput
              field={toInvoiceField}
              theme="detailed"
              value={moveToId}
              onChange={setMoveToId}
            />
            <SolidActionButton
              isLoading={moveLoading}
              onClick={onMove}
              opacity={canMove ? 1 : 0.7}
              ml="auto"
              gap={1}
              isDisabled={!canMove}
              size="sm">
              Move Payment
            </SolidActionButton>
          </Flex>
        )}
      </VStack>
    </Flex>
  )
}

export const MovePaymentToInvoicePopover = ({
  invoice,
  assessmentId,
  paymentId,
  initialFromId,
  initialToId,
}: Omit<MovePaymentProps, 'onClose'>) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <Popover
      closeOnBlur={false}
      isOpen={isOpen}
      onClose={onClose}
      isLazy
      onOpen={onOpen}
      strategy="fixed">
      <PopoverTrigger>
        <Flex>
          <Tooltip
            placement="top"
            hasArrow
            label={`Move ${paymentId === 'deposits' ? 'deposits' : 'payment'} to another invoice`}
            aria-label="Move payment">
            <IconButton
              aria-label="Move payment"
              borderLeftRadius={0}
              size="xs"
              color={colors.green.hex}
              variant="ghost"
              colorScheme="gray"
              icon={<ArrowForwardIcon width={5} height={5} />}
            />
          </Tooltip>
        </Flex>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="340px">
          <PopoverArrow />
          <PopoverBody p={0}>
            <PopoverCloseButton />
            <MovePayment
              invoice={invoice}
              assessmentId={assessmentId}
              paymentId={paymentId}
              initialFromId={initialFromId}
              initialToId={initialToId}
              onClose={onClose}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

const AddPayment = (props: AddPaymentProps) => {
  const {
    addToId: moveToId,
    setAddToId: setMoveToId,
    toInvoiceField,
    hasUnsentInvoices,
    addLoading: moveLoading,
    canAdd: canMove,
    onAdd: onMove,
  } = useAddPayment(props)

  return (
    <Flex flexFlow="column" w="100%">
      <Flex p={2} borderBottom="1px solid #e0e0e0" w="100%" justify="space-between">
        <Text lineHeight={1} fontWeight={500} fontSize="md">
          Add Payment
        </Text>
      </Flex>
      <VStack bg="gray.50" px={3} pb={2} position="relative" align="start" spacing={2}>
        {!hasUnsentInvoices ? (
          <Box pt={1}>
            <Text fontStyle="italic" opacity={0.7} fontSize="md">
              No other unsent invoices
            </Text>
          </Box>
        ) : (
          <Flex pt={1} w="100%" flexFlow="column">
            <Text fontSize="md">To Invoice</Text>
            <StandaloneInput
              field={toInvoiceField}
              theme="detailed"
              value={moveToId}
              onChange={setMoveToId}
            />
            <SolidActionButton
              isLoading={moveLoading}
              onClick={onMove}
              opacity={canMove ? 1 : 0.7}
              ml="auto"
              gap={1}
              isDisabled={!canMove}
              size="sm">
              Add Payment
            </SolidActionButton>
          </Flex>
        )}
      </VStack>
    </Flex>
  )
}

export const AddPaymentToInvoicePopover = (props: Omit<AddPaymentProps, 'onClose'>) => {
  const { paymentId } = props
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <Popover
      closeOnBlur={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      isLazy
      strategy="fixed">
      <PopoverTrigger>
        <Flex>
          <Tooltip
            placement="top"
            hasArrow
            label={`Add ${paymentId === 'deposits' ? 'deposits' : 'payment'} to invoice`}
            aria-label="Add payment">
            <IconButton
              aria-label="Add payment"
              borderLeftRadius={0}
              size="xs"
              color={colors.green.hex}
              variant="ghost"
              colorScheme="gray"
              icon={<AddIcon width={3} height={3} />}
            />
          </Tooltip>
        </Flex>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="340px">
          <PopoverArrow />
          <PopoverBody p={0}>
            <PopoverCloseButton />
            <AddPayment onClose={onClose} {...props} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
